import { SortingState } from '@tanstack/react-table';
import { citation, FilterSchemas, ScreenerFilter } from '@toggle/toggle';

import { ActiveFilterOptions } from '~/components/filters/Filters';
import { FilterOption } from '~/components/update-filter-options/updateFilterOptions';
import {
  DocumentSearchFilterGroupKeys,
  FilterGroups,
  SharedFilterState,
} from '~/hooks/use-filter-actions/useFilterActions';
import { postEntity } from '~/services/entities/entity-service';
import { apiFetch } from '~/utils/api-fetch/apiFetch';
import { create } from '~/utils/store/createStore';

const COMPANY_FILTER_KEY = 'company';
const SOURCE_FILTER_KEY = 'source';
const SOURCE_FILTER_INDEX = 0;

export type SourcesType = 'company' | 'country' | 'all';

export type StatisticsCounters = {
  documents: number;
  countries: number;
  companies: number;
  citations: number;
};
export type Statistics = {
  company?: StatisticsCounters;
  country?: StatisticsCounters;
};

const getSourceType = (key: string): SourcesType => {
  if (['company', 'country', 'all'].includes(key)) {
    return key as SourcesType;
  }

  return 'country';
};

const visibilityFilter = (f: { visible: boolean }) => f.visible;

export type DocumentSourcesTableStore = {
  sorting: {
    id: string;
    desc: boolean;
  }[];
  currentGroupId?: string;
  handleColumnSorting: (newSorting: SortingState) => void;
  resetColumnSorting: () => void;
  updateFilters: (args: {
    groupId?: string;
    filter?: ActiveFilterOptions | {};
  }) => void;
  clearAllActiveFilterOptions: () => void;
  resetFilters: () => void;
  activeDocumentIds: string[];
  setActiveDocumentIds: (ids: string[]) => void;
  customContextCounts?: {
    documentsCount: number;
    companiesCount: number;
    statistics: Statistics;
  };
  setCustomContextCounts: (counts?: {
    statistics: Statistics;
    documentsCount: number;
    companiesCount: number;
  }) => void;
  hasVisibleSourceFilter: boolean;
  selectedSourceFilter: SourcesType;
  firstFilter?: ScreenerFilter;
  onFilterGroupsChange: (
    filterGroups: FilterGroups<DocumentSearchFilterGroupKeys>,
    group?: string
  ) => void;
} & SharedFilterState<DocumentSearchFilterGroupKeys>;

const defaultColumnSorting = [{ id: 'rank', desc: true }];

const fetchAndProcessFilterSchemas = async ({
  groupId,
  filter = {},
}: {
  groupId?: string;
  filter?: {
    filters?: {
      source: {
        values: [Record<string, unknown>];
      };
    };
  };
}) => {
  if (!groupId) {
    return null;
  }

  const response = await apiFetch<FilterSchemas>(
    citation.citationFilterSchemas.path(groupId),
    {
      method: 'post',
      body: filter,
    }
  );

  if (response.error) {
    return null;
  }

  const { schemas } = response.data.groups[0];

  return await Promise.all(
    schemas.map(async schema => {
      if (schema.key !== COMPANY_FILTER_KEY) {
        return schema;
      }

      const options = schema.config.options ?? [];
      const updatedOptions = await Promise.all(
        options.map(async option => {
          const { data } = await postEntity(option.key);
          return { ...option, name: data?.name ?? option.name, entity: data };
        })
      );

      return {
        ...schema,
        config: { ...schema.config, options: updatedOptions },
      };
    })
  );
};

const getDefaultDocumentFilterGroups = () => {
  return {
    documentFilters: {
      activeFilterOptions: [],
      activeFilters: [],
      allFilters: [],
    },
  };
};

const getSchemaPayloadFilter = (
  filter: FilterOption
): {
  filters: {
    source: {
      values: [Record<string, unknown>];
    };
  };
} => {
  return {
    filters: {
      source: {
        values: [filter.value],
      },
    },
  };
};

const getSourceFilter = (filterOptions: ActiveFilterOptions[]) => {
  return filterOptions.find(filter => filter.id === 'source');
};

export const useDocumentSourcesTableStore = create<DocumentSourcesTableStore>(
  (set, get) => ({
    hasVisibleSourceFilter: false,
    selectedSourceFilter: 'all',
    currentGroupId: undefined,
    resetFilters: () => {
      set({
        filterGroups: getDefaultDocumentFilterGroups(),
        selectedSourceFilter: 'all',
      });
    },
    lockedSourceFilters: [],
    updateFilters: async ({ groupId, filter = {} }) => {
      const { resetFilters } = get();

      if (!groupId) {
        resetFilters();
        return;
      }

      const updatedSchemas = await fetchAndProcessFilterSchemas({
        groupId,
        filter,
      });

      if (updatedSchemas) {
        const firstFilter = updatedSchemas[SOURCE_FILTER_INDEX] || {};
        const isSourceFilterVisible =
          firstFilter.key === SOURCE_FILTER_KEY && firstFilter.visible;
        const activeFilters = isSourceFilterVisible
          ? [firstFilter]
          : updatedSchemas.filter(visibilityFilter);

        set({
          currentGroupId: groupId,
          firstFilter: firstFilter,
          selectedSourceFilter: 'all',
          hasVisibleSourceFilter: isSourceFilterVisible,
          filterGroups: {
            documentFilters: {
              allFilters: updatedSchemas,
              activeFilterOptions: [],
              activeFilters: activeFilters,
            },
          },
        });
      }
    },
    onFilterGroupsChange: async filterGroups => {
      const {
        hasVisibleSourceFilter,
        currentGroupId,
        selectedSourceFilter,
        firstFilter,
        updateFilters,
        clearAllActiveFilterOptions,
      } = get();
      const {
        documentFilters: { activeFilterOptions },
      } = filterGroups;
      const sourceFilter = getSourceFilter(activeFilterOptions);

      if (!hasVisibleSourceFilter) {
        set({ filterGroups });
        return;
      }

      if (!sourceFilter && hasVisibleSourceFilter) {
        clearAllActiveFilterOptions();
        return;
      }

      if (!activeFilterOptions.length || !sourceFilter) {
        updateFilters({ groupId: currentGroupId });
        return;
      }

      const sourceFilterOption = sourceFilter.options[0];
      const newSelectedSourceFilter = getSourceType(sourceFilterOption.key);

      const sourceFilterChanged =
        newSelectedSourceFilter !== selectedSourceFilter;

      if (sourceFilterChanged) {
        const updatedSchemas = await fetchAndProcessFilterSchemas({
          groupId: currentGroupId,
          filter: getSchemaPayloadFilter(sourceFilterOption),
        });

        if (updatedSchemas && firstFilter) {
          const activeFilters = updatedSchemas.filter(visibilityFilter);

          set({
            selectedSourceFilter: newSelectedSourceFilter,
            filterGroups: {
              documentFilters: {
                ...filterGroups.documentFilters,
                activeFilterOptions: [activeFilterOptions[SOURCE_FILTER_INDEX]],
                activeFilters: [firstFilter, ...activeFilters],
              },
            },
          });
        }
      } else {
        set({ filterGroups });
      }
    },
    filterGroups: getDefaultDocumentFilterGroups(),
    sorting: defaultColumnSorting,
    handleColumnSorting: (newSorting: SortingState) => {
      set({ sorting: newSorting });
    },
    resetColumnSorting: () => {
      set({ sorting: defaultColumnSorting });
    },
    clearAllActiveFilterOptions: () =>
      set(state => {
        const { firstFilter, hasVisibleSourceFilter } = state;

        const documentFilters = {
          ...state.filterGroups.documentFilters,
          activeFilters: firstFilter ? [firstFilter] : [],
          activeFilterOptions: [],
        };

        const filterGroups = hasVisibleSourceFilter
          ? { documentFilters }
          : {
              documentFilters: {
                ...state.filterGroups.documentFilters,
                activeFilterOptions: [],
              },
            };

        return { filterGroups, selectedSourceFilter: 'all' };
      }),
    activeDocumentIds: [],
    setActiveDocumentIds: ids => {
      set({ activeDocumentIds: ids });
    },
    setCustomContextCounts: counts => {
      set({ customContextCounts: counts });
    },
  })
);
